import { Form, Formik, type FormikHelpers, useField } from "formik";
import { Check, Monitor, Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { RichTextEditor } from "@/components/common/rich-text-editor";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  listPractitioners,
  type PractitionerOption,
} from "@/features/practitioners/api";
import {
  LANGUAGE_OPTIONS,
  type SettingsUser,
  TIME_ZONE_OPTIONS,
  updatePreferences,
} from "@/features/settings/api";
import { FIELD_LABEL } from "@/features/settings/components/settings-section";
import { SelectField } from "@/features/settings/components/select-field";
import { ApiError } from "@/lib/api";
import { textToHtml } from "@/lib/rich-text";
import { sanitizeHtml } from "@/lib/sanitize";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/store/ui-store";

// Sentinel for "no default practitioner" — Radix Select forbids empty-string
// item values, so null <-> this string at the form boundary.
const NO_PRACTITIONER = "__none__";

const TIME_ZONE_VALUES = TIME_ZONE_OPTIONS.map((t) => t.value);

const profileSchema = Yup.object({
  name: Yup.string().trim().required("Name is required").max(255, "Name is too long"),
  language: Yup.string().oneOf(["en", "es"]).required(),
  timeZone: Yup.string().oneOf(TIME_ZONE_VALUES, "Choose a time zone").required(),
  emailSignature: Yup.string().max(2000, "Signature is too long"),
  defaultPractitioner: Yup.string().required(),
});

interface ProfileValues {
  name: string;
  language: "en" | "es";
  timeZone: string;
  emailSignature: string;
  defaultPractitioner: string;
}

/** Appearance (theme) picker — applies instantly via next-themes; not part of
 * the Formik "Save changes" form. */
function AppearancePicker() {
  const { theme, setTheme } = useTheme();
  const active = theme ?? "system";
  const options = [
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
    { value: "system", label: "Match system", icon: Monitor },
  ];
  return (
    <div className="flex flex-col gap-2">
      <span className={FIELD_LABEL}>Appearance</span>
      <div role="group" aria-label="Appearance" className="grid gap-2.5 sm:grid-cols-3">
        {options.map(({ value, label, icon: Icon }) => {
          const selected = active === value;
          return (
            <button
              key={value}
              type="button"
              aria-pressed={selected}
              onClick={() => setTheme(value)}
              className={cn(
                "flex flex-col gap-2 rounded-md border-2 p-2.5 text-left transition-colors",
                selected ? "border-ring" : "border-border hover:border-muted-foreground/40",
              )}
            >
              <span className="bg-muted flex h-14 items-center justify-center rounded-[9px] border">
                <Icon className="text-muted-foreground size-5" />
              </span>
              <span className="flex items-center justify-between text-[13px] font-semibold">
                {label}
                {selected && <Check className="text-info size-3.5" />}
              </span>
            </button>
          );
        })}
      </div>
      <p className="text-muted-foreground text-xs">
        Dark mode is easier on the eyes for evening sessions — it changes nothing for your
        teammates or clients.
      </p>
    </div>
  );
}

/** A single client-side preference toggle (persisted in the UI store). */
function ToggleRow({
  title,
  description,
  checked,
  onCheckedChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  onCheckedChange: (on: boolean) => void;
}) {
  return (
    <label className="bg-muted/50 flex cursor-pointer items-center justify-between gap-3 rounded-md px-3.5 py-3">
      <span className="flex flex-col gap-0.5 leading-tight">
        <span className="text-[13px] font-semibold">{title}</span>
        <span className="text-muted-foreground text-xs">{description}</span>
      </span>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </label>
  );
}

/** Client-side display preferences (theme + the UI-store toggles). Instant-apply;
 * independent of the Formik form below. */
function PreferenceToggles() {
  const compactTables = useUIStore((s) => s.compactTables);
  const setCompactTables = useUIStore((s) => s.setCompactTables);
  const desktopNotifications = useUIStore((s) => s.desktopNotifications);
  const setDesktopNotifications = useUIStore((s) => s.setDesktopNotifications);
  const reduceMotion = useUIStore((s) => s.reduceMotion);
  const setReduceMotion = useUIStore((s) => s.setReduceMotion);

  function handleDesktopNotifications(on: boolean) {
    setDesktopNotifications(on);
    // Ask the browser for permission when enabling (best-effort; the flag is
    // kept regardless so the choice persists).
    if (on && typeof Notification !== "undefined" && Notification.permission === "default") {
      void Notification.requestPermission();
    }
  }

  return (
    <div className="grid gap-2.5 sm:grid-cols-2">
      <ToggleRow
        title="Compact rows in tables"
        description="Fit more clients and documents per screen"
        checked={compactTables}
        onCheckedChange={setCompactTables}
      />
      <ToggleRow
        title="Desktop notifications"
        description="Chat mentions and deadline alerts in your browser"
        checked={desktopNotifications}
        onCheckedChange={handleDesktopNotifications}
      />
      <ToggleRow
        title="Reduce motion"
        description="Turn off background animation and transitions"
        checked={reduceMotion}
        onCheckedChange={setReduceMotion}
      />
    </div>
  );
}

/** Formik-bound rich-text editor for the signature, with a live client-facing
 * preview beside it. */
function SignatureField() {
  const [field, meta, helpers] = useField<string>("emailSignature");
  const hasError = meta.touched && !!meta.error;
  const previewHtml = sanitizeHtml(field.value || "");
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="grid gap-2">
        <RichTextEditor
          id="emailSignature"
          ariaLabel="Email Signature"
          ariaInvalid={hasError}
          minHeight={160}
          value={field.value}
          onChange={(html) => {
            helpers.setValue(html);
            if (!meta.touched) helpers.setTouched(true, false);
          }}
        />
        <p className="text-muted-foreground text-xs">Appended to client emails and letters.</p>
        {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
      </div>
      <div className="bg-muted/50 flex flex-col gap-2.5 rounded-md p-4">
        <span className={FIELD_LABEL}>How clients see it</span>
        <div className="bg-card rounded-md border p-4 text-[13px]">
          <p className="text-muted-foreground mb-3">
            …happy to walk you through the response before we send it.
          </p>
          {previewHtml ? (
            // Sanitized above via sanitizeHtml (DOMPurify) — safe to render.
            <div
              className="prose prose-sm dark:prose-invert max-w-none"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          ) : (
            <p className="text-muted-foreground italic">Your signature preview appears here.</p>
          )}
        </div>
      </div>
    </div>
  );
}

interface ProfileFormProps {
  user: SettingsUser;
  onSaved: (user: SettingsUser) => void;
}

export function ProfileForm({ user, onSaved }: ProfileFormProps) {
  const isFirm = user.accountType === "firm";
  const [practitioners, setPractitioners] = useState<PractitionerOption[]>([]);

  useEffect(() => {
    if (!isFirm) return;
    let active = true;
    listPractitioners()
      .then((list) => {
        if (active) setPractitioners(list);
      })
      .catch(() => {
        // Non-fatal: the dropdown just shows only "None" if the list fails.
      });
    return () => {
      active = false;
    };
  }, [isFirm]);

  const practitionerOptions = [
    { value: NO_PRACTITIONER, label: "— None —" },
    ...practitioners.map((p) => ({ value: p.id, label: p.name })),
  ];

  async function handleSubmit(
    values: ProfileValues,
    { resetForm }: FormikHelpers<ProfileValues>,
  ) {
    try {
      const updated = await updatePreferences({
        name: values.name.trim(),
        language: values.language,
        timeZone: values.timeZone,
        emailSignature: values.emailSignature,
        defaultPractitioner:
          values.defaultPractitioner === NO_PRACTITIONER ? null : values.defaultPractitioner,
      });
      onSaved(updated);
      // Re-baseline the form to what we just saved so `dirty` clears (the form
      // isn't reinitialized from props — that would wipe an in-progress edit
      // when an avatar upload refreshes the shared user state).
      resetForm({ values });
      toast.success("Changes saved");
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Couldn't save changes.";
      toast.error(message);
    }
  }

  return (
    <Formik<ProfileValues>
      initialValues={{
        name: user.name,
        language: user.language,
        timeZone: user.timeZone,
        // Legacy signatures are plain text — upgrade to HTML for the editor.
        emailSignature: textToHtml(user.emailSignature),
        defaultPractitioner: user.defaultPractitioner ?? NO_PRACTITIONER,
      }}
      validationSchema={profileSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, dirty, resetForm }) => (
        <Form className="flex flex-col gap-6">
          {/* About you */}
          <Card className="scroll-mt-24 gap-5 p-6">
            <h2 className="text-[17px] font-semibold tracking-tight">About you</h2>
            <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2">
              <FormField
                name="name"
                label="Full name"
                autoComplete="name"
                labelClassName={FIELD_LABEL}
              />
              <div className="grid gap-2">
                <Label htmlFor="email" className={FIELD_LABEL}>
                  Sign-in email
                </Label>
                <Input id="email" value={user.email} disabled />
                <p className="text-muted-foreground text-xs">
                  Your email is your sign-in identity and can&apos;t be changed here.
                </p>
              </div>
            </div>
          </Card>

          {/* Preferences */}
          <Card id="preferences" className="scroll-mt-24 gap-5 p-6">
            <h2 className="text-[17px] font-semibold tracking-tight">Your workspace, your way</h2>
            <AppearancePicker />
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <SelectField
                name="language"
                label="Language"
                options={LANGUAGE_OPTIONS}
                labelClassName={FIELD_LABEL}
              />
              <SelectField
                name="timeZone"
                label="Time zone"
                options={TIME_ZONE_OPTIONS}
                labelClassName={FIELD_LABEL}
              />
              {isFirm && (
                <SelectField
                  name="defaultPractitioner"
                  label="Default practitioner for e-Services"
                  options={practitionerOptions}
                  labelClassName={FIELD_LABEL}
                />
              )}
            </div>
            <div className="bg-border h-px" />
            <PreferenceToggles />
          </Card>

          {/* Email signature */}
          <Card id="signature" className="scroll-mt-24 gap-4 p-6">
            <div>
              <h2 className="text-[17px] font-semibold tracking-tight">Email signature</h2>
              <p className="text-muted-foreground text-[13px]">
                Appended to client emails and letters you send.
              </p>
            </div>
            <SignatureField />
          </Card>

          {/* Sticky save bar */}
          <div className="bg-card sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-5 py-3.5 shadow-card">
            <span className="text-muted-foreground inline-flex items-center gap-2.5 text-[13px]">
              <span
                className={cn(
                  "size-2 rounded-full",
                  dirty ? "bg-primary" : "bg-muted-foreground/30",
                )}
              />
              {dirty ? (
                <>
                  <strong className="text-foreground font-semibold">Unsaved changes</strong> · profile
                  and signature
                </>
              ) : (
                "All changes saved"
              )}
            </span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={!dirty || isSubmitting}
                onClick={() => resetForm()}
              >
                Discard
              </Button>
              <Button type="submit" disabled={!dirty || isSubmitting}>
                {isSubmitting ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
